import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Search, User, Briefcase, Calendar, AlertTriangle,
  ExternalLink, Save, GripVertical, MessageSquare, MoreHorizontal, Megaphone, RefreshCw,
  Telescope, Orbit, PhoneCall, CheckCircle2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { usePmTestMode } from "../hooks/usePmTestMode";

// Lane identity. pm_id is null for BOTH the On the Horizon and Unassigned lanes,
// so board_kind — not pm_id — is what tells them apart. Everything that keys a
// column (order, drop target, refs) goes through boardKey.
const HORIZON_KEY = "horizon";
const UNASSIGNED_KEY = "unassigned";

const boardKey = (board) =>
  board?.board_kind === "horizon" ? HORIZON_KEY : (board?.pm_id || UNASSIGNED_KEY);

const isHorizonBoard = (board) => board?.board_kind === "horizon";
// Not a VEXOS Tracker stage — the VEXOS card's "take it off the board" option.
const VEXOS_CLOSE = "__close__";

const STAGE_COLORS = {
  "Queued": "bg-slate-100 text-slate-700",
  "Kickoff": "bg-sky-100 text-sky-700",
  "Production": "bg-amber-100 text-amber-700",
  "On Hold": "bg-zinc-300 text-zinc-700",
  "Delivered": "bg-green-100 text-green-700",
  "Closed": "bg-zinc-200 text-zinc-600"
};

export default function ManagementView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [boards, setBoards] = useState([]);
  const [stages, setStages] = useState([]);
  const [vexosStages, setVexosStages] = useState([]);
  
  // PM Column order (persisted per user)
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem("management-pm-column-order");
    return saved ? JSON.parse(saved) : [];
  });
  
  // Dragging state for columns vs programs
  const [draggingType, setDraggingType] = useState(null); // "column" or "program"
  
  // PM Test Mode
  const pmTestMode = usePmTestMode();
  
  // Query params for navigation from program cards
  const highlightProgramId = searchParams.get("highlight");
  const targetPmId = searchParams.get("pm");
  const pmColumnRefs = useRef({});
  
  // Filters
  const [search, setSearch] = useState("");
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [includeOnHold, setIncludeOnHold] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // DnD
  const [activeProgram, setActiveProgram] = useState(null);
  const [activeColumn, setActiveColumn] = useState(null);
  
  // Side panel
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const isCOO = user?.role === "COO";

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  // Custom collision detection: when dragging columns, only detect column collisions
  const customCollisionDetection = useCallback((args) => {
    const { active } = args;
    const activeId = String(active.id);
    
    if (activeId.startsWith("column:")) {
      // When dragging a column, only consider other columns as drop targets
      const columnCollisions = rectIntersection({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          container => String(container.id).startsWith("column:")
        ),
      });
      return columnCollisions;
    }
    
    // For programs, use closestCenter but only with PM drop zones
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter(
        container => String(container.id).startsWith("pm:")
      ),
    });
  }, []);

  useEffect(() => {
    fetchBoards();
  }, [includeCompleted, includeOnHold, pmTestMode.pmId]);

  // Scroll to PM column and highlight program when navigated from program card
  useEffect(() => {
    if (!loading && boards.length > 0 && targetPmId) {
      const pmColumn = pmColumnRefs.current[targetPmId];
      if (pmColumn) {
        pmColumn.scrollIntoView({ behavior: "smooth", inline: "center", block: "start" });
      }
    }
  }, [loading, boards, targetPmId]);

  // silent: reload the lanes in place. A horizon change moves a card between two
  // lanes and restates both counts, so it refetches instead of patching state —
  // but without the full-view spinner, which would flash on every drag.
  async function fetchBoards({ silent = false } = {}) {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("include_completed", includeCompleted);
      params.append("include_on_hold", includeOnHold);
      if (search) params.append("search", search);
      
      const response = await apiClient.get(`/management/pm-boards?${params}`);
      let fetchedBoards = response.data.boards;
      
      // Note: In PM Test Mode, we still show ALL PM columns
      // Only financial data is hidden, not the PM columns themselves
      
      // Default lane order: On the Horizon, then Unassigned, then PMs A-Z.
      // Saved orders predate the horizon lane, so a key missing from the saved
      // list falls back to this rank instead of being dumped on the far right.
      const defaultRank = (board) => {
        const key = boardKey(board);
        if (key === HORIZON_KEY) return -2;
        if (key === UNASSIGNED_KEY) return -1;
        return 999;
      };
      const byDefault = (a, b) => {
        const diff = defaultRank(a) - defaultRank(b);
        if (diff !== 0) return diff;
        return (a.pm_name || "").localeCompare(b.pm_name || "");
      };

      if (columnOrder.length > 0) {
        const orderMap = new Map(columnOrder.map((id, idx) => [id, idx]));
        const rank = (board) => {
          const key = boardKey(board);
          return orderMap.has(key) ? orderMap.get(key) : defaultRank(board);
        };
        fetchedBoards.sort((a, b) => {
          const diff = rank(a) - rank(b);
          return diff !== 0 ? diff : byDefault(a, b);
        });
      } else {
        fetchedBoards.sort(byDefault);
      }
      
      setBoards(fetchedBoards);
      setStages(response.data.stages);
      setVexosStages(response.data.vexos_stages || []);
    } catch (error) {
      toast.error("Failed to load management boards");
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function handleForceRefresh() {
    setRefreshing(true);
    await fetchBoards();
    setRefreshing(false);
    toast.success("Boards refreshed");
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    fetchBoards();
  }

  function handleDragStart(event) {
    const activeId = String(event.active.id);
    
    // Check if dragging a column (starts with "column:")
    if (activeId.startsWith("column:")) {
      setDraggingType("column");
      const columnId = activeId.replace("column:", "");
      const board = boards.find(b => boardKey(b) === columnId);
      setActiveColumn(board);
    } else {
      // Dragging a program
      setDraggingType("program");
      const program = findProgramById(activeId);
      setActiveProgram(program);
    }
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    
    if (draggingType === "column") {
      setActiveColumn(null);
      setDraggingType(null);
      
      if (!over) return;
      
      const activeId = String(active.id).replace("column:", "");
      const overId = String(over.id).replace("column:", "");
      
      if (activeId !== overId) {
        // Find indices in visualizationBoards (the ones being sorted)
        const vizBoards = boards.filter(b => 
          !(b.pm_id === "matheus_silva" || b.pm_name?.toLowerCase().includes("matheus"))
        );
        
        const oldIndex = vizBoards.findIndex(b => boardKey(b) === activeId);
        const newIndex = vizBoards.findIndex(b => boardKey(b) === overId);
        
        if (oldIndex !== -1 && newIndex !== -1) {
          // Reorder visualization boards
          const reorderedVizBoards = arrayMove(vizBoards, oldIndex, newIndex);
          
          // Get marketing board (stays at end, not part of reordering)
          const marketingB = boards.find(b => 
            b.pm_id === "matheus_silva" || b.pm_name?.toLowerCase().includes("matheus")
          );
          
          // Combine: reordered viz boards + marketing board (if exists)
          const newBoards = marketingB 
            ? [...reorderedVizBoards, marketingB]
            : reorderedVizBoards;
          
          setBoards(newBoards);
          
          // Save order to localStorage (only viz boards order matters)
          const newOrder = reorderedVizBoards.map(boardKey);
          setColumnOrder(newOrder);
          localStorage.setItem("management-pm-column-order", JSON.stringify(newOrder));
          toast.success("Column order saved");
        }
      }
      return;
    }
    
    // Handle program drag
    setActiveProgram(null);
    setDraggingType(null);
    
    if (!over) return;
    
    const program = findProgramById(active.id);
    if (!program) return;

    try {
      // Target is pm:{pmId}, pm:null (Unassigned) or pm:horizon
      const target = String(over.id).replace("pm:", "");

      // Into On the Horizon: flag it and leave the PM assignment alone, so
      // clearing the flag later drops the card back into that PM's lane.
      if (target === HORIZON_KEY) {
        if (!program.onHorizon) handleHorizonChange(program, true);
        return;
      }

      const newPmId = target === "null" ? null : target;
      // The VEXOS PM's column holds VEXOS cards only — no visualization work.
      if (newPmId && boards.find(b => b.pm_id === newPmId)?.is_vexos_pm) {
        toast.error("That column is VEXOS-only — assign the project to a visualization PM");
        return;
      }
      // Normalize currentPmId for comparison (treat undefined as null)
      const currentPmNormalized = program.currentPmId ?? null;
      const newPmNormalized = newPmId ?? null;
      const isReassign = currentPmNormalized !== newPmNormalized;

      // Out of On the Horizon: the project has signed. Clearing the flag is open
      // to any editor, but a move to a DIFFERENT PM is still a reassignment, so
      // it keeps the COO-only rule rather than half-applying the gesture.
      if (program.onHorizon) {
        if (isReassign && !isCOO) {
          toast.error("Only COO can reassign programs between PMs");
          return;
        }
        handleHorizonChange(program, false, isReassign ? newPmId : undefined);
        return;
      }

      // Only reassign if actually moving to a different column
      if (isReassign) {
        handlePMReassign(program, newPmId);
      }
    } catch (error) {
      console.error("Drag end error:", error);
      toast.error("Failed to process drag action");
    }
  }

  function findProgramById(programId) {
    for (const board of boards) {
      const found = board.programs.find(p => p.id === programId);
      if (found) {
        return {
          ...found,
          // A horizon card sits outside the PM lanes, so its real PM travels on
          // the program (assigned_pm_id) rather than on the board it came from.
          currentPmId: isHorizonBoard(board) ? (found.assigned_pm_id ?? null) : board.pm_id,
          onHorizon: isHorizonBoard(board),
        };
      }
    }
    return null;
  }

  async function handleHorizonChange(program, onHorizon, reassignToPmId) {
    const oldBoards = boards;
    // Name the lane it lands in while the pre-change boards are still in state.
    const landedIn = reassignToPmId === undefined
      ? null
      : (boards.find(b => !isHorizonBoard(b) && (b.pm_id ?? null) === (reassignToPmId ?? null))?.pm_name
         || "Unassigned");
    try {
      await apiClient.post("/management/set-horizon", {
        program_id: program.id,
        on_horizon: onHorizon
      });
      if (reassignToPmId !== undefined) {
        // Same gesture, second decision: the card was dropped on another PM.
        await apiClient.post("/management/reassign-pm", {
          from_pm_id: program.currentPmId || null,
          to_pm_id: reassignToPmId,
          program_id: program.id
        });
      }
      toast.success(
        onHorizon
          ? `${program.program_name} moved to On the Horizon — out of financial totals`
          : landedIn
            // Both halves of the gesture, so the reassignment isn't silent.
            ? `${program.program_name} is no longer on the horizon — back in financial totals, now with ${landedIn}`
            : `${program.program_name} is no longer on the horizon — back in financial totals`
      );
      await fetchBoards({ silent: true });
    } catch (error) {
      console.error("Horizon change error:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update On the Horizon");
      setBoards(oldBoards);
    }
  }

  async function handlePMReassign(program, newPmId) {
    if (!isCOO) {
      toast.error("Only COO can reassign programs between PMs");
      return;
    }
    
    if (!program || !program.id) {
      toast.error("Invalid program");
      return;
    }
    
    const oldBoards = JSON.parse(JSON.stringify(boards));
    
    // Pre-calculate target name before state changes
    // Handle null/undefined comparison for "Unassigned" column
    const targetBoard = boards.find(b => 
      newPmId === null ? (!b.pm_id || b.pm_id === null) : b.pm_id === newPmId
    );
    const targetName = targetBoard?.pm_name || "Unassigned";
    
    try {
      // Optimistic UI update with robust null handling
      setBoards(prev => {
        const updated = prev.map(board => {
          // Check if this is the source board (handle null/undefined for Unassigned)
          const isSourceBoard = program.currentPmId === null || program.currentPmId === undefined
            ? (!board.pm_id || board.pm_id === null)
            : board.pm_id === program.currentPmId;
          
          return {
            ...board,
            programs: board.programs.filter(p => p.id !== program.id),
            active_program_count: isSourceBoard
              ? Math.max(0, (board.active_program_count || 0) - 1)
              : board.active_program_count || 0
          };
        });
        
        // Find target board with null handling for "Unassigned"
        const targetBoardIdx = updated.findIndex(b => 
          newPmId === null ? (!b.pm_id || b.pm_id === null) : b.pm_id === newPmId
        );
        
        if (targetBoardIdx !== -1) {
          updated[targetBoardIdx] = {
            ...updated[targetBoardIdx],
            programs: [...updated[targetBoardIdx].programs, { ...program, currentPmId: newPmId }],
            active_program_count: (updated[targetBoardIdx].active_program_count || 0) + 1
          };
        }
        
        return updated;
      });
      
      await apiClient.post("/management/reassign-pm", {
        from_pm_id: program.currentPmId || null,
        to_pm_id: newPmId,
        program_id: program.id
      });
      
      toast.success(`Reassigned to ${targetName}`);
    } catch (error) {
      console.error("Reassign error:", error);
      // Handle various error response formats
      const errorDetail = error.response?.data?.detail;
      let errorMessage = "Failed to reassign";
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e).join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      toast.error(errorMessage);
      setBoards(oldBoards);
    }
  }

  async function handleStageChange(program, newStage) {
    if (program.stage === newStage) return;
    
    setBoards(prev => prev.map(board => ({
      ...board,
      programs: board.programs.map(p => 
        p.id === program.id ? { ...p, stage: newStage } : p
      )
    })));
    
    try {
      await apiClient.patch(`/management/update-stage/${program.id}?stage=${newStage}`);
      toast.success(`Stage updated to ${newStage}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update stage");
      fetchBoards();
    }
  }

  // The VEXOS card. Its stage IS the project's VEXOS Tracker stage (same
  // endpoint as the VEXOS tab, so Final Delivery still readies the
  // subscription); due date and closing the card are board-only state.
  function patchVexosCard(card, changes) {
    setBoards(prev => prev.map(board => board.vexos_cards ? {
      ...board,
      vexos_cards: board.vexos_cards.map(c => c.id === card.id ? { ...c, ...changes } : c),
    } : board));
  }

  async function handleVexosStageChange(card, stage) {
    if (stage === VEXOS_CLOSE) {
      setBoards(prev => prev.map(board => board.vexos_cards ? {
        ...board, vexos_cards: board.vexos_cards.filter(c => c.id !== card.id),
      } : board));
      try {
        await apiClient.patch(`/management/vexos-ops/${card.id}`, { closed: true });
        toast.success(`${card.program_name} taken off the VEXOS board`);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to close the VEXOS card");
        fetchBoards();
      }
      return;
    }
    if (card.vexos_stage === stage) return;
    patchVexosCard(card, { vexos_stage: stage });
    try {
      await apiClient.put(`/programs/${card.id}/vexos-tracker`, { stage });
      toast.success(`VEXOS stage updated to ${stage}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update VEXOS stage");
      fetchBoards();
    }
  }

  async function handleVexosDueDate(card, dueDate) {
    patchVexosCard(card, { vexos_due_date: dueDate });
    try {
      await apiClient.patch(`/management/vexos-ops/${card.id}`, { due_date: dueDate });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save the VEXOS due date");
      fetchBoards();
    }
  }

  function openProgramPanel(program, pmId, onHorizon = false) {
    setSelectedProgram({
      ...program,
      // A horizon card is shown outside the PM lanes, so take its PM from the
      // program rather than from the column it was clicked in.
      currentPmId: onHorizon ? (program.assigned_pm_id ?? null) : pmId,
      onHorizon,
    });
    setEditData({
      stage: program.stage || "Intake",
      end_window: program.end_window || "",
      notes: program.notes || "",
      waiting_on_client: program.waiting_on_client || false
    });
  }

  async function saveProgram() {
    if (!selectedProgram) return;
    setSaving(true);
    try {
      await apiClient.patch(`/programs/${selectedProgram.id}`, editData);
      toast.success("Program updated");
      setBoards(prev => prev.map(board => ({
        ...board,
        programs: board.programs.map(p => 
          p.id === selectedProgram.id ? { ...p, ...editData } : p
        )
      })));
      setSelectedProgram(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  }

  async function addNote() {
    if (!selectedProgram || !editData.notes) return;
    const timestamp = new Date().toLocaleString();
    const newNote = `[${timestamp}] ${editData.notes}`;
    const existingNotes = selectedProgram.notes || "";
    const combinedNotes = existingNotes ? `${existingNotes}\n\n${newNote}` : newNote;
    
    setSaving(true);
    try {
      await apiClient.patch(`/programs/${selectedProgram.id}`, { notes: combinedNotes });
      toast.success("Note logged");
      setBoards(prev => prev.map(board => ({
        ...board,
        programs: board.programs.map(p => 
          p.id === selectedProgram.id ? { ...p, notes: combinedNotes } : p
        )
      })));
      setEditData({ ...editData, notes: "" });
    } catch (error) {
      toast.error("Failed to log note");
    } finally {
      setSaving(false);
    }
  }

  // Separate Marketing PM (Matheus) from Visualization PMs
  // Matheus is identified by pm_id "matheus_silva" or name containing "matheus"
  const isMarketingPM = (board) => 
    board.pm_id === "matheus_silva" || 
    board.pm_name?.toLowerCase().includes("matheus");
  
  const marketingBoard = boards.find(b => isMarketingPM(b));
  const visualizationBoards = boards.filter(b => !isMarketingPM(b));

  // Column IDs for sortable context (visualization PMs only)
  const columnIds = visualizationBoards.map(b => `column:${boardKey(b)}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col gap-6" data-testid="management-view">
        {/* Header */}
        <div className="flex-shrink-0">
          <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
            Management View
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Drag columns to reorder • Drag programs between PMs to reassign • Click card to edit
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Drop a project in <span className="font-medium text-estate-700">On the Horizon</span> when
            the client hasn't signed or paid yet — it stays out of every financial total until it's
            dragged back out.
          </p>
        </div>

        {/* Filters */}
        <div className="flex-shrink-0">
          <FiltersBar
            search={search}
            setSearch={setSearch}
            onSearchSubmit={handleSearchSubmit}
            includeCompleted={includeCompleted}
            setIncludeCompleted={setIncludeCompleted}
            includeOnHold={includeOnHold}
            setIncludeOnHold={setIncludeOnHold}
            onRefresh={handleForceRefresh}
            refreshing={refreshing}
          />
        </div>

        {/* Visualization PMs - Single horizontal row layout */}
        <div className="flex-shrink-0">
          <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'var(--font-sans)' }}>
            Visualization PMs
          </h2>
          <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
            <div 
              className="flex flex-row flex-nowrap gap-3 overflow-x-auto pb-2"
              style={{
                // Calculate lane width based on number of managers
                '--lane-count': visualizationBoards.length,
              }}
            >
              {visualizationBoards.map(board => (
                <SortablePMColumn
                  key={boardKey(board)}
                  board={board}
                  stages={stages}
                  onProgramClick={(p) => openProgramPanel(p, board.pm_id, isHorizonBoard(board))}
                  onStageChange={handleStageChange}
                  vexosStages={vexosStages}
                  onVexosStageChange={handleVexosStageChange}
                  onVexosDueDate={handleVexosDueDate}
                  onOpenProject={(id) => navigate(`/programs/${id}`)}
                  onOpenProfile={() => board.pm_id && navigate(`/studio/resources/${board.pm_id}`)}
                  isCOO={isCOO}
                  isDropTarget={
                    activeProgram && !board.is_vexos_pm &&
                    (isHorizonBoard(board)
                      ? !activeProgram.onHorizon
                      : activeProgram.onHorizon || activeProgram.currentPmId !== board.pm_id)
                  }
                  columnRef={(el) => { if (board.pm_id) pmColumnRefs.current[board.pm_id] = el; }}
                  highlightProgramId={highlightProgramId}
                  laneCount={visualizationBoards.length}
                />
              ))}
            </div>
          </SortableContext>
        </div>

        {/* Marketing Programs Widget */}
        {marketingBoard && (
          <div className="flex-shrink-0 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 rounded-lg bg-pink-100">
                <Megaphone className="w-4 h-4 text-pink-600" />
              </div>
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>
                Marketing Programs
              </h2>
              <Badge variant="secondary" className="text-xs">
                {marketingBoard.programs.length} programs
              </Badge>
            </div>
            <div className="overflow-x-auto pb-4">
              <PMColumn
                board={marketingBoard}
                stages={stages}
                onProgramClick={(p) => openProgramPanel(p, marketingBoard.pm_id)}
                onStageChange={handleStageChange}
                onOpenProfile={() => marketingBoard.pm_id && navigate(`/studio/resources/${marketingBoard.pm_id}`)}
                isCOO={isCOO}
                isDropTarget={activeProgram && activeProgram.currentPmId !== marketingBoard.pm_id}
                columnRef={(el) => { if (marketingBoard.pm_id) pmColumnRefs.current[marketingBoard.pm_id] = el; }}
                highlightProgramId={highlightProgramId}
                isMarketingSection={true}
              />
            </div>
          </div>
        )}

        {/* Drag Overlay */}
        <DragOverlay dropAnimation={{ duration: 200, easing: "ease" }}>
          {activeProgram && <DragPreviewCard program={activeProgram} />}
          {activeColumn && <DragPreviewColumn board={activeColumn} />}
        </DragOverlay>

        {/* Side Panel */}
        <Sheet open={!!selectedProgram} onOpenChange={() => setSelectedProgram(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            {selectedProgram && (
              <ProgramEditPanel
                program={selectedProgram}
                editData={editData}
                setEditData={setEditData}
                stages={stages}
                onSave={saveProgram}
                onAddNote={addNote}
                onHorizonChange={async (next) => {
                  await handleHorizonChange(selectedProgram, next);
                  setSelectedProgram(prev => prev && { ...prev, onHorizon: next });
                }}
                saving={saving}
                isCOO={isCOO}
              />
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DndContext>
  );
}

function FiltersBar(props) {
  const { search, setSearch, onSearchSubmit, includeCompleted, setIncludeCompleted,
    includeOnHold, setIncludeOnHold, onRefresh, refreshing } = props;

  return (
    <div className="flex flex-wrap gap-3 items-center p-3 bg-muted/40 rounded-xl border">
      <form onSubmit={onSearchSubmit} className="flex gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 w-[180px] h-9 bg-background"
          />
        </div>
        <Button type="submit" variant="secondary" size="sm">Go</Button>
      </form>
      
      <div className="h-5 w-px bg-border" />
      
      <div className="flex items-center gap-2">
        <Checkbox id="delivered" checked={includeCompleted} onCheckedChange={setIncludeCompleted} />
        <Label htmlFor="delivered" className="text-xs cursor-pointer">Delivered</Label>
      </div>
      
      <div className="flex items-center gap-2">
        <Checkbox id="paused" checked={includeOnHold} onCheckedChange={setIncludeOnHold} />
        <Label htmlFor="paused" className="text-xs cursor-pointer">Paused</Label>
      </div>
      
      <div className="h-5 w-px bg-border" />

      <Button
        variant="outline"
        size="sm"
        onClick={onRefresh}
        disabled={refreshing}
        className="gap-1.5 h-9"
        data-testid="refresh-boards-btn"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        Refresh
      </Button>
    </div>
  );
}

// Sortable wrapper for PM columns (enables drag reordering)
function SortablePMColumn({ board, laneCount = 8, ...props }) {
  const columnId = boardKey(board);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `column:${columnId}`,
  });

  // Calculate responsive width based on lane count
  // Try to fit all lanes on screen, with min-width for readability
  const getWidthStyle = () => {
    if (laneCount <= 4) {
      return { flex: '1 1 0', minWidth: '240px', maxWidth: '320px' };
    } else if (laneCount <= 6) {
      return { flex: '1 1 0', minWidth: '200px', maxWidth: '280px' };
    } else if (laneCount <= 8) {
      return { flex: '1 1 0', minWidth: '180px', maxWidth: '240px' };
    } else {
      return { flex: '0 0 auto', width: '180px', minWidth: '180px' };
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    ...getWidthStyle(),
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group flex-shrink-0">
      {/* Drag handle for column - visible on hover */}
      <div 
        {...attributes} 
        {...listeners}
        className="absolute top-2 left-1/2 -translate-x-1/2 z-20 cursor-grab active:cursor-grabbing bg-white hover:bg-gray-50 rounded-full shadow-md border px-2 py-1 opacity-0 group-hover:opacity-100 transition-all hover:scale-110"
        title="Drag to reorder columns"
      >
        <GripVertical className="w-4 h-4 text-gray-500" />
      </div>
      <PMColumn board={board} laneCount={laneCount} {...props} />
    </div>
  );
}

// Preview card for dragging columns
function DragPreviewColumn({ board }) {
  return (
    <div className="w-[280px] rounded-xl border border-primary bg-background shadow-xl opacity-90">
      <div className="p-4 border-b bg-muted/50 rounded-t-xl">
        <div className="flex items-center gap-3">
          {board.profile_image_url ? (
            <img 
              src={board.profile_image_url} 
              alt={board.pm_name}
              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
            />
          ) : (
            <div className="p-2 rounded-lg bg-purple-100">
              <User className="w-5 h-5 text-purple-600" />
            </div>
          )}
          <div>
            <h2 className="font-semibold text-sm">{board.pm_name}</h2>
            <span className="text-xs text-muted-foreground">{board.programs.length} programs</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PMColumn({ board, stages, onProgramClick, onStageChange, vexosStages = [], onVexosStageChange, onVexosDueDate, onOpenProject, onOpenProfile, isCOO, isDropTarget, columnRef, highlightProgramId, isMarketingSection = false, laneCount = 8 }) {
  const isHorizon = isHorizonBoard(board);
  const isUnassigned = !board.pm_id && !isHorizon;
  const pmId = isHorizon ? HORIZON_KEY : (board.pm_id || "null");

  const { setNodeRef, isOver } = useDroppable({
    id: `pm:${pmId}`,
  });

  // Combine refs for both droppable and column ref
  const combinedRef = (el) => {
    setNodeRef(el);
    if (columnRef) columnRef(el);
  };

  // Sort programs by stage (pipeline order)
  const sortedPrograms = [...board.programs].sort(
    (a, b) => (stages.indexOf(a.stage) || 0) - (stages.indexOf(b.stage) || 0)
  );

  // Calculate activity alert for each program
  const getActivityAlert = (program) => {
    if (!program.last_activity_at) return null;
    const lastActivity = new Date(program.last_activity_at);
    const now = new Date();
    const daysSinceActivity = Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24));
    
    if (daysSinceActivity >= 30) return { level: 'red', days: daysSinceActivity };
    if (daysSinceActivity >= 14) return { level: 'yellow', days: daysSinceActivity };
    return null;
  };

  // Count alerts for this column
  const alerts = sortedPrograms.reduce((acc, p) => {
    const alert = getActivityAlert(p);
    if (alert?.level === 'red') acc.red++;
    if (alert?.level === 'yellow') acc.yellow++;
    return acc;
  }, { red: 0, yellow: 0 });

  // Compact mode for many lanes
  const isCompact = laneCount > 6;

  // VEXOS PM's column only: one card per VEXOS project, in VEXOS pipeline order.
  const vexosCards = [...(board.vexos_cards || [])].sort(
    (a, b) => vexosStages.indexOf(a.vexos_stage) - vexosStages.indexOf(b.vexos_stage)
  );

  return (
    <div
      ref={combinedRef}
      className={`
        flex flex-col w-full h-full rounded-lg border transition-all
        ${isHorizon
          ? "border-dashed border-estate-200 bg-estate-50/60"
          : isUnassigned
            ? "border-dashed border-slate-300 bg-slate-50/50"
            : isMarketingSection
              ? "border-pink-200 bg-pink-50/30"
              : "border-border bg-background"
        }
        ${isOver && (isCOO || isHorizon) ? "ring-2 ring-primary ring-inset scale-[1.01]" : ""}
      `}
      data-testid={`pm-column-${pmId}`}
    >
      {/* PM Header - Compact */}
      <div className={`
        flex-shrink-0 ${isCompact ? 'p-2.5' : 'p-3'} border-b rounded-t-lg
        ${isHorizon ? "bg-estate-100" : isUnassigned ? "bg-slate-100" : isMarketingSection ? "bg-pink-100/50" : "bg-muted/50"}
      `}>
        <div className="flex items-start justify-between gap-1">
          <div className="flex items-center gap-2 min-w-0">
            {/* Profile Photo or Fallback Icon - Smaller in compact mode */}
            {board.profile_image_url ? (
              <img 
                src={board.profile_image_url} 
                alt={board.pm_name}
                className={`${isCompact ? 'w-8 h-8' : 'w-9 h-9'} rounded-full object-cover flex-shrink-0 border-2 border-white shadow-sm`}
              />
            ) : (
              <div className={`${isCompact ? 'p-1.5' : 'p-2'} rounded-lg flex-shrink-0 ${
                isHorizon ? "bg-estate-100" :
                isUnassigned ? "bg-slate-200" :
                isMarketingSection ? "bg-pink-200" : "bg-purple-100"
              }`}>
                {isHorizon ? (
                  <Telescope className={`${isCompact ? 'w-4 h-4' : 'w-5 h-5'} text-estate-700`} />
                ) : isUnassigned ? (
                  <User className={`${isCompact ? 'w-4 h-4' : 'w-5 h-5'} text-slate-500`} />
                ) : isMarketingSection ? (
                  <Megaphone className={`${isCompact ? 'w-4 h-4' : 'w-5 h-5'} text-pink-600`} />
                ) : (
                  <User className={`${isCompact ? 'w-4 h-4' : 'w-5 h-5'} text-purple-600`} />
                )}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h2 className={`font-semibold ${isCompact ? 'text-xs' : 'text-sm'} truncate`}>{board.pm_name}</h2>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                {board.pm_type && (
                  <Badge variant="secondary" className="text-[9px] font-medium px-1 py-0">
                    {board.pm_type}
                  </Badge>
                )}
                {/* Activity Alerts */}
                {alerts.red > 0 && (
                  <span className="flex items-center gap-0.5 text-[9px] text-red-600 font-medium">
                    <AlertTriangle className="w-2.5 h-2.5" />
                    {alerts.red}
                  </span>
                )}
                {alerts.yellow > 0 && (
                  <span className="flex items-center gap-0.5 text-[9px] text-yellow-600 font-medium">
                    <AlertTriangle className="w-2.5 h-2.5" />
                    {alerts.yellow}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {!isUnassigned && !isHorizon && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0">
                  <MoreHorizontal className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onOpenProfile}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Profile
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        {/* Stats - More compact */}
        <div className="flex items-center gap-2 mt-2">
          {board.is_vexos_pm ? (
            <span className={`${isCompact ? 'text-[10px]' : 'text-xs'} text-muted-foreground`}>
              <span className="font-semibold text-foreground">{vexosCards.length}</span> VEXOS projects
            </span>
          ) : (
            <span className={`${isCompact ? 'text-[10px]' : 'text-xs'} text-muted-foreground`}>
              <span className="font-semibold text-foreground">{board.active_program_count}</span> programs
            </span>
          )}
        </div>

        {isHorizon && (
          <p className={`${isCompact ? 'text-[9px]' : 'text-[10px]'} text-estate-700 leading-snug mt-1`}>
            Not signed or paid — excluded from financial totals
          </p>
        )}
      </div>
      
      {/* Programs List - Vertical Stack (all visible) - Tighter spacing */}
      <div className={`${isCompact ? 'p-2 space-y-1.5' : 'p-2.5 space-y-2'}`}>
        {sortedPrograms.map(program => (
          <DraggableProgramCard
            key={program.id}
            program={program}
            stages={stages}
            onClick={() => onProgramClick(program)}
            onStageChange={onStageChange}
            isHighlighted={program.id === highlightProgramId}
            isCompact={isCompact}
            isHorizon={isHorizon}
          />
        ))}

        {sortedPrograms.length === 0 && vexosCards.length === 0 && (
          <div className={`flex items-center justify-center text-center ${isCompact ? 'h-20' : 'h-24'} text-[10px] text-muted-foreground/60 px-2`}>
            {isDropTarget && (isCOO || isHorizon)
              ? "Drop program here"
              : isHorizon
                ? "Nothing on the horizon"
                : board.is_vexos_pm
                  ? "No VEXOS projects"
                  : "No programs"}
          </div>
        )}

        {vexosCards.length > 0 && (
          <>
            <div className={`flex items-center gap-1.5 ${sortedPrograms.length ? 'pt-2' : ''} text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}>
              <Orbit className="w-3 h-3" />
              VEXOS track
            </div>
            {vexosCards.map(card => (
              <VexosTrackCard
                key={`vexos:${card.id}`}
                card={card}
                stages={vexosStages}
                onStageChange={onVexosStageChange}
                onDueDate={onVexosDueDate}
                onOpen={() => onOpenProject(card.id)}
                isCompact={isCompact}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function DraggableProgramCard({ program, stages, onClick, onStageChange, isHighlighted, isCompact = false, isHorizon = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: program.id,
  });
  const [showScope, setShowScope] = useState(false);
  const [scopeData, setScopeData] = useState(null);
  const [loadingScope, setLoadingScope] = useState(false);
  const hoverTimerRef = useRef(null);

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 100,
  } : undefined;

  const stageColor = STAGE_COLORS[program.stage] || STAGE_COLORS["Intake"];
  const dueDate = program.end_window ? new Date(program.end_window) : null;
  const isOverdue = dueDate && dueDate < new Date();

  // Activity alert calculation
  const getActivityAlert = () => {
    if (!program.last_activity_at) return null;
    const lastActivity = new Date(program.last_activity_at);
    const now = new Date();
    const daysSinceActivity = Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24));
    
    if (daysSinceActivity >= 30) return { level: 'red', days: daysSinceActivity };
    if (daysSinceActivity >= 14) return { level: 'yellow', days: daysSinceActivity };
    return null;
  };
  
  const activityAlert = getActivityAlert();

  // Timeline calculations
  const startDate = program.start_window ? new Date(program.start_window) : null;
  const endDate = program.end_window ? new Date(program.end_window) : null;
  const today = new Date();
  
  let progressPercent = 0;
  let daysRemaining = 0;
  
  if (startDate && endDate) {
    const daysTotal = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const daysElapsed = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
    daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    progressPercent = Math.min(100, Math.max(0, (daysElapsed / daysTotal) * 100));
  }

  // Fetch scope data with delay to avoid interfering with drag
  const handleMouseEnter = () => {
    // Clear any existing timer
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    
    // Only show popup after 600ms delay (enough time to start dragging)
    hoverTimerRef.current = setTimeout(async () => {
      if (isDragging) return; // Don't show if dragging started
      
      setShowScope(true);
      if (!scopeData && !loadingScope) {
        setLoadingScope(true);
        try {
          const [milestonesRes, scopeModulesRes] = await Promise.all([
            apiClient.get(`/programs/${program.id}/milestones`),
            apiClient.get(`/programs/${program.id}/scope-modules`)
          ]);
          setScopeData({
            milestones: milestonesRes.data || [],
            scopeModules: scopeModulesRes.data || []
          });
        } catch (err) {
          console.error("Failed to load scope data");
        }
        setLoadingScope(false);
      }
    }, 600);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setShowScope(false);
  };

  // Hide popup when dragging starts
  useEffect(() => {
    if (isDragging) {
      setShowScope(false);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    }
  }, [isDragging]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        group relative bg-white rounded-lg border shadow-sm transition-all
        hover:shadow-md hover:border-primary/30
        ${isDragging ? "opacity-40 scale-95" : ""}
        ${program.waiting_on_client ? "border-l-[3px] border-l-amber-400" : ""}
        ${activityAlert?.level === 'red' ? "border-l-[3px] border-l-red-500" : ""}
        ${activityAlert?.level === 'yellow' && !program.waiting_on_client ? "border-l-[3px] border-l-yellow-500" : ""}
        ${isHighlighted ? "ring-2 ring-primary ring-offset-1 animate-pulse" : ""}
      `}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`program-card-${program.id}`}
    >
      {/* Activity Alert Indicator */}
      {activityAlert && (
        <div 
          className={`absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${
            activityAlert.level === 'red' 
              ? 'bg-red-100 text-red-700' 
              : 'bg-yellow-100 text-yellow-700'
          }`}
          title={`No activity for ${activityAlert.days} days`}
        >
          <AlertTriangle className="w-2.5 h-2.5" />
          {activityAlert.days}d
        </div>
      )}
      
      {/* Scope of Work Hover Popup - Hidden during drag */}
      {showScope && !isDragging && (
        <div 
          className="fixed z-[100] w-[450px] bg-white border border-gray-200 rounded-xl shadow-2xl animate-in fade-in zoom-in-95 duration-150 pointer-events-none"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
          {/* Header with Timeline */}
          <div className="p-4 border-b bg-gray-50 rounded-t-xl">
            <h3 className="font-semibold text-sm mb-2">{program.program_name}</h3>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" />
              <span>{startDate?.toLocaleDateString("en-US", { month: "short", day: "numeric" })} → {endDate?.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
              <span className={`ml-auto font-medium ${isOverdue ? 'text-red-600' : 'text-green-600'}`}>
                {isOverdue ? `${Math.abs(daysRemaining)}d overdue` : `${daysRemaining}d left`}
              </span>
            </div>
            <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${isOverdue ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          
          {/* Scope Content */}
          <div className="p-4 max-h-[350px] overflow-y-auto">
            {loadingScope ? (
              <div className="text-center py-6 text-muted-foreground text-sm">
                Loading scope...
              </div>
            ) : scopeData?.milestones?.length > 0 ? (
              <div className="space-y-4">
                {/* Group milestones by scope_type */}
                {Object.entries(
                  scopeData.milestones.reduce((acc, m) => {
                    const scope = m.scope_type || 'UNCATEGORIZED';
                    if (!acc[scope]) acc[scope] = [];
                    acc[scope].push(m);
                    return acc;
                  }, {})
                ).map(([scopeType, milestones]) => (
                  <div key={scopeType} className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-3 py-2 flex items-center justify-between">
                      <span className="font-medium text-xs uppercase tracking-wide">{scopeType}</span>
                      <span className="text-xs text-muted-foreground">{milestones.length} items</span>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-medium">Deliverable</th>
                          <th className="text-center px-2 py-1.5 font-medium w-12">Qty</th>
                          <th className="text-center px-2 py-1.5 font-medium w-16">Status</th>
                          <th className="text-left px-2 py-1.5 font-medium w-24">Due</th>
                        </tr>
                      </thead>
                      <tbody>
                        {milestones.map(m => (
                          <tr key={m.id} className="border-t">
                            <td className="px-3 py-1.5 truncate max-w-[160px]">{m.title || m.deliverable_name || 'Untitled'}</td>
                            <td className="text-center px-2 py-1.5">{m.quantity || 1}</td>
                            <td className="text-center px-2 py-1.5">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                m.status === 'complete' ? 'bg-green-100 text-green-700' :
                                m.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {m.status || 'pending'}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-[10px] text-muted-foreground">
                              {m.due_date ? new Date(m.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            ) : scopeData?.scopeModules?.filter(sm => sm.is_included).length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground mb-2">Scope modules configured:</p>
                {scopeData.scopeModules.filter(sm => sm.is_included).map(sm => (
                  <div key={sm.id} className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-1 bg-gray-100 rounded font-medium">{sm.scope_type}</span>
                    {sm.assigned_pm_name && <span className="text-muted-foreground">→ {sm.assigned_pm_name}</span>}
                  </div>
                ))}
                <p className="text-xs text-muted-foreground italic mt-2">No deliverables added yet</p>
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">
                No scope configured
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Drag Handle */}
      <div 
        {...listeners} 
        {...attributes}
        className={`absolute left-0 top-0 bottom-0 ${isCompact ? 'w-6' : 'w-8'} flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted/50 rounded-l-lg`}
      >
        <GripVertical className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'} text-muted-foreground`} />
      </div>
      
      {/* Content */}
      <div className={`${isCompact ? 'pl-6 pr-2 py-2' : 'pl-8 pr-3 py-3'} cursor-pointer`} onClick={onClick}>
        <div className="flex items-start justify-between gap-1 mb-1">
          <span className={`${isCompact ? 'text-[11px]' : 'text-[13px]'} font-medium leading-tight line-clamp-2 flex-1`}>
            {program.program_name}
          </span>
        </div>

        <div className="flex items-center gap-1 mb-1.5">
          <p className={`${isCompact ? 'text-[10px]' : 'text-[11px]'} text-muted-foreground truncate flex-1 min-w-0`}>
            {program.client_name}
          </p>
          <ScopeIcons program={program} isCompact={isCompact} />
        </div>
        
        {/* Stage Selector */}
        <div className="flex items-center justify-between gap-1">
          <Select 
            value={program.stage || "Intake"} 
            onValueChange={(v) => { onStageChange(program, v); }}
          >
            <SelectTrigger 
              className={`${isCompact ? 'h-5 text-[9px] px-1.5' : 'h-6 text-[10px] px-2'} w-auto min-w-0 ${stageColor} border-0`}
              onClick={(e) => e.stopPropagation()}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {stages.map(s => (
                <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {dueDate && (
            <span className={`flex items-center gap-0.5 ${isCompact ? 'text-[9px]' : 'text-[10px]'} flex-shrink-0 ${
              isOverdue ? "text-red-600 font-medium" : "text-muted-foreground"
            }`}>
              <Calendar className={`${isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3'}`} />
              {dueDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
          )}
        </div>
        
        {program.waiting_on_client && (
          <Badge variant="outline" className={`${isCompact ? 'text-[8px] px-1 py-0' : 'text-[9px]'} bg-amber-50 border-amber-200 text-amber-700 mt-1.5`}>
            Waiting on Client
          </Badge>
        )}

        {/* On the horizon: the lane a card returns to once the client signs, so a
            PM can pick their own upcoming work out of the shared column. */}
        {isHorizon && (
          <p className={`${isCompact ? 'text-[9px]' : 'text-[10px]'} text-estate-700 mt-1.5 truncate`}>
            {program.assigned_pm_name ? `Prepping: ${program.assigned_pm_name}` : "No PM yet"}
          </p>
        )}
      </div>
    </div>
  );
}

// What was sold beyond visualization, at a glance: VEXOS (ink, the VEXOS brand
// axis) and Marketing (pink, same as the Marketing Programs section).
function ScopeIcons({ program, isCompact = false }) {
  if (!program.has_vexos && !program.has_marketing) return null;
  const chip = `${isCompact ? 'w-4 h-4' : 'w-5 h-5'} inline-flex items-center justify-center rounded-full flex-shrink-0`;
  const icon = isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3';
  return (
    <div className="flex items-center gap-0.5 flex-shrink-0">
      {program.has_vexos && (
        <span className={`${chip} bg-ink text-white ring-1 ring-inset ring-white/30`} title="Includes VEXOS" data-testid="vexos-scope-badge">
          <Orbit className={icon} />
        </span>
      )}
      {program.has_marketing && (
        <span className={`${chip} bg-pink-100 text-pink-700`} title="Includes Marketing" data-testid="marketing-scope-badge">
          <Megaphone className={icon} />
        </span>
      )}
    </div>
  );
}

// A project's VEXOS work, shown in the VEXOS PM's column alongside (not instead
// of) its visualization card. The stage is the project's VEXOS Tracker stage;
// the due date is the card's own. It stays on the board after the
// visualization side is delivered, until taken off. Not draggable — the card
// belongs to the VEXOS column.
function VexosTrackCard({ card, stages, onStageChange, onDueDate, onOpen, isCompact = false }) {
  const dueDate = card.vexos_due_date ? new Date(`${card.vexos_due_date}T00:00:00`) : null;
  const isDelivered = card.vexos_stage === stages[stages.length - 1];
  const isOverdue = dueDate && dueDate < new Date() && !isDelivered;
  const contacted = !!card.client_contacted_at;
  const text = isCompact ? 'text-[9px]' : 'text-[10px]';

  return (
    <div
      className="relative bg-white rounded-lg border border-l-[3px] border-l-ink dark:border-l-white/70 shadow-sm hover:shadow-md transition-all"
      data-testid={`vexos-track-card-${card.id}`}
    >
      <div className={`${isCompact ? 'px-2 py-2' : 'px-3 py-3'} cursor-pointer`} onClick={onOpen}>
        <div className="flex items-start justify-between gap-1 mb-1">
          <span className={`${isCompact ? 'text-[11px]' : 'text-[13px]'} font-medium leading-tight line-clamp-2 flex-1`}>
            {card.program_name}
          </span>
        </div>

        <div className="flex items-center gap-1 mb-1.5">
          <p className={`${isCompact ? 'text-[10px]' : 'text-[11px]'} text-muted-foreground truncate flex-1 min-w-0`}>
            {card.client_name}
          </p>
          <ScopeIcons program={card} isCompact={isCompact} />
        </div>

        {/* Has the VEXOS PM reached out yet — marked from the project's VEXOS tab. */}
        <div
          className={`flex items-center gap-1 w-fit max-w-full ${text} font-medium mb-1.5 ${
            contacted ? "text-green-700" : "rounded px-1.5 py-0.5 bg-orange-100 text-orange-700"
          }`}
          title={contacted ? `Client contacted ${new Date(card.client_contacted_at).toLocaleDateString()}${card.client_contacted_by ? ` by ${card.client_contacted_by}` : ""}` : "Mark it on the project's VEXOS tab"}
          data-testid="vexos-client-contacted"
        >
          {contacted ? <CheckCircle2 className="w-3 h-3 flex-shrink-0" /> : <PhoneCall className="w-3 h-3 flex-shrink-0" />}
          <span className="truncate">{contacted ? "Client contacted" : "Client not contacted yet"}</span>
        </div>

        <div className="flex items-center justify-between gap-1">
          <Select value={card.vexos_stage} onValueChange={(v) => onStageChange(card, v)}>
            <SelectTrigger
              className={`${isCompact ? 'h-5 text-[9px] px-1.5' : 'h-6 text-[10px] px-2'} w-auto min-w-0 border-0 ${
                isDelivered ? "bg-green-100 text-green-700" : "bg-ink text-white"
              }`}
              onClick={(e) => e.stopPropagation()}
              data-testid="vexos-stage-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {stages.map(s => (
                <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
              ))}
              <SelectItem value={VEXOS_CLOSE} className="text-xs text-muted-foreground">Take off board</SelectItem>
            </SelectContent>
          </Select>

          <label
            className={`relative flex items-center gap-0.5 ${text} flex-shrink-0 cursor-pointer ${
              isOverdue ? "text-red-600 font-medium" : "text-muted-foreground"
            }`}
            title="VEXOS due date"
            onClick={(e) => e.stopPropagation()}
          >
            <Calendar className={`${isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3'}`} />
            {dueDate ? dueDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "Set date"}
            <input
              type="date"
              value={card.vexos_due_date || ""}
              onChange={(e) => onDueDate(card, e.target.value || null)}
              onClick={(e) => { try { e.currentTarget.showPicker(); } catch { /* older browsers open on focus */ } }}
              className="absolute inset-0 opacity-0 cursor-pointer"
              data-testid="vexos-due-date"
            />
          </label>
        </div>

        {/* Where the visualization side stands, so the VEXOS PM doesn't have to
            leave the column to see it. */}
        <p className={`${text} text-muted-foreground mt-1.5 truncate`}>
          Viz: {card.viz_stage || "—"}{card.viz_pm_name ? ` · ${card.viz_pm_name}` : ""}
        </p>
      </div>
    </div>
  );
}

function DragPreviewCard({ program }) {
  return (
    <div className="bg-white rounded-lg border-2 border-primary shadow-xl p-3 w-[260px] rotate-2 scale-105">
      <div className="flex items-start gap-2 mb-1.5">
        <span className="text-[13px] font-medium leading-tight line-clamp-2 flex-1">
          {program.program_name}
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground truncate">
        {program.client_name}
      </p>
    </div>
  );
}

function ProgramEditPanel({ program, editData, setEditData, stages, onSave, onAddNote, onHorizonChange, saving, isCOO }) {
  const [horizonSaving, setHorizonSaving] = useState(false);
  const onHorizon = !!program.onHorizon;

  return (
    <>
      <SheetHeader className="pb-4 border-b">
        <SheetTitle className="text-lg font-semibold">{program.program_name}</SheetTitle>
        <SheetDescription className="text-sm">{program.client_name}</SheetDescription>
      </SheetHeader>

      <div className="space-y-6 py-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            label="Contract Value"
            value={isCOO ? `$${(program.contract_value || 0).toLocaleString()}` : "Financials restricted"}
            restricted={!isCOO}
            note={isCOO && onHorizon ? "Not counted — on the horizon" : null}
          />
          <StatCard label="Stage" value={program.stage || "Intake"} />
        </div>

        {/* On the Horizon — same write path as dragging into the lane, so it
            applies immediately rather than waiting for Save Changes. */}
        <div className={`rounded-lg border p-3 ${onHorizon ? "border-estate-200 bg-estate-50/60" : "border-border bg-muted/30"}`}>
          <div className="flex items-start gap-3">
            <Checkbox
              id="on-horizon"
              checked={onHorizon}
              disabled={horizonSaving}
              onCheckedChange={async (next) => {
                setHorizonSaving(true);
                try {
                  await onHorizonChange(!!next);
                } finally {
                  setHorizonSaving(false);
                }
              }}
            />
            <div className="space-y-1">
              <Label htmlFor="on-horizon" className="text-sm cursor-pointer flex items-center gap-1.5">
                <Telescope className="w-3.5 h-3.5 text-estate-700" />
                On the Horizon
              </Label>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Client hasn't signed or paid yet. Keeps the project in HQ so the team can
                prepare, and out of every financial total until it's contracted.
              </p>
            </div>
          </div>
        </div>

        {/* Editable Fields */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Stage</Label>
            <Select value={editData.stage} onValueChange={v => setEditData({ ...editData, stage: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Expected Delivery</Label>
            <Input
              type="date"
              value={editData.end_window ? editData.end_window.split("T")[0] : ""}
              onChange={e => setEditData({ ...editData, end_window: e.target.value })}
            />
          </div>
          
          <div className="flex items-center gap-3 py-2">
            <Checkbox
              id="waiting"
              checked={editData.waiting_on_client}
              onCheckedChange={v => setEditData({ ...editData, waiting_on_client: v })}
            />
            <Label htmlFor="waiting" className="text-sm cursor-pointer">Waiting on Client</Label>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Notes</Label>
              <Button variant="ghost" size="sm" onClick={onAddNote} disabled={!editData.notes || saving} className="h-6 text-xs gap-1">
                <MessageSquare className="w-3 h-3" />
                Log Update
              </Button>
            </div>
            <Textarea
              value={editData.notes || ""}
              onChange={e => setEditData({ ...editData, notes: e.target.value })}
              placeholder="Add an update or note..."
              rows={3}
              className="resize-none"
            />
            {program.notes && (
              <div className="mt-2 p-3 bg-muted/50 rounded-lg max-h-[120px] overflow-y-auto">
                <p className="text-[11px] text-muted-foreground whitespace-pre-wrap">{program.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex gap-2 pt-4 border-t">
        <Button onClick={onSave} disabled={saving} className="flex-1 gap-2">
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
        <Button 
          variant="outline" 
          onClick={() => window.open(`/programs/${program.id}`, "_blank")}
          className="gap-2"
        >
          <ExternalLink className="w-4 h-4" />
          Full View
        </Button>
      </div>
    </>
  );
}

function StatCard({ label, value, restricted, note }) {
  return (
    <div className={`p-3 rounded-lg ${restricted ? "bg-muted/30" : "bg-muted/50"}`}>
      <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-base font-semibold ${restricted ? "text-muted-foreground italic text-sm" : ""}`}>
        {value}
      </p>
      {note && <p className="text-[10px] text-estate-700 mt-0.5 leading-snug">{note}</p>}
    </div>
  );
}
